import React, { FC, RefObject } from 'react';

import {
  ENTITLEMENTS_PLATFORM_TYPES,
  EntitlementsProject,
} from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import {
  LogoAutodeskAccIcon,
  LogoAutodeskBim360Icon,
} from '@adsk/alloy-react-icon';
import { MenuItem } from '@adsk/alloy-react-menu';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import HighlightedItemText from './HighlightedItemText';
import { MFAIcon } from './MFAIcon';
import getAnchorTarget from '../helpers/getAnchorTarget';
import getProjectUrl from '../helpers/getProjectUrl';

type ProjectListTemplateItemProps = {
  item: EntitlementsProject;
  search?: string;
  currentProjectId?: string;
  currentProductId?: string;
  onClose?: () => void;
  onProjectClick?: (project?: EntitlementsProject, url?: string) => void;
  handleAnalyticOnClick?: (project?: EntitlementsProject) => void;
  containerRef?: RefObject<HTMLDivElement>;
};

const ProjectListTemplateItem: FC<ProjectListTemplateItemProps> = ({
  item: template,
  search,
  currentProjectId,
  currentProductId,
  onClose,
  onProjectClick,
  handleAnalyticOnClick,
  containerRef,
}) => {
  const id = template.id;
  const url = getProjectUrl(template, currentProductId);

  const highlighted = currentProjectId === id;

  const LogoComponent =
    template.platform === ENTITLEMENTS_PLATFORM_TYPES.ACC
      ? LogoAutodeskAccIcon
      : LogoAutodeskBim360Icon;

  return (
    <MenuItem
      data-testid={`template-item ${highlighted ? 'highlighted' : ''}`}
      rel="noreferrer noopener"
      highlighted={highlighted}
      showSelection={false}
      as={onProjectClick ? 'button' : 'a'}
      href={url}
      target={getAnchorTarget(url)}
      onClick={() => {
        onProjectClick?.(template);
        handleAnalyticOnClick?.(template);
        onClose?.();
      }}
      style={{
        height: 64,
        padding: '8px 16px',
        textAlign: 'initial',
      }}
    >
      <Flex flex={1} alignItems="center">
        <Box flex={1}>
          <Flex alignItems="center">
            <Box flexDirection="column" maxWidth="250px">
              <Box
                typography="heading4"
                color={highlighted ? 'white' : 'charcoal900'}
              >
                <OverflowTooltip content={template.name} singleLine={false}>
                  <HighlightedItemText
                    text={template.name as string}
                    search={search}
                    highlighted={highlighted}
                  />
                </OverflowTooltip>
              </Box>

              <Box
                typography="caption"
                color={highlighted ? 'white' : 'charcoal600'}
                ellipsis="single"
              >
                {template.accountName}
              </Box>
            </Box>
            {template.mfaBlocked && <MFAIcon containerRef={containerRef} />}
          </Flex>
        </Box>

        <LogoComponent
          size={16}
          style={{
            color: highlighted ? theme.colors.white : theme.colors.acsLogoBlue,
            marginLeft: theme.spacing.medium,
          }}
        />
      </Flex>
    </MenuItem>
  );
};

ProjectListTemplateItem.displayName = 'ProjectListTemplateItem';

export default ProjectListTemplateItem;
